import React, { useEffect, useState } from "react";
import api, { API, getAccessToken, fileDownloadUrl, formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, CheckCircle2, PlayCircle, FileText, ListChecks, FileVideo, Download,
  Lock, Award, Circle, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
}

const LTYPE_ICON = { video: FileVideo, pdf: FileText, text: FileText, resource: Download };

function QuizTaker({ courseId, quiz, result, onGraded }) {
  const { t } = useT();
  const [answers, setAnswers] = useState({});
  const [graded, setGraded] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setSingle = (qid, idx) => setAnswers((a) => ({ ...a, [qid]: idx }));
  const toggleMulti = (qid, idx) => setAnswers((a) => {
    const cur = new Set(a[qid] || []);
    cur.has(idx) ? cur.delete(idx) : cur.add(idx);
    return { ...a, [qid]: [...cur] };
  });
  const setText = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/training/courses/${courseId}/quiz/${quiz.id}/submit`, { answers });
      setGraded(data);
      onGraded(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const retake = () => { setAnswers({}); setGraded(null); };

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><ListChecks className="w-5 h-5 text-primary" /></div>
        <div>
          <h2 className="font-display text-xl font-bold">{quiz.title}</h2>
          <p className="text-xs text-muted-foreground">{t("passingGrade")}: {quiz.passing_grade}%</p>
        </div>
      </div>

      {graded && (
        <div className={`p-4 rounded-xl border ${graded.passed ? "border-emerald-500/40 bg-emerald-500/10" : "border-destructive/40 bg-destructive/10"}`} data-testid="quiz-result">
          <p className="font-bold text-lg">{graded.passed ? "✓ " + t("passed") : t("failed")}</p>
          <p className="text-sm">{t("yourScore")}: <b>{graded.score}%</b> ({t("passingGrade")}: {graded.passing_grade}%)</p>
        </div>
      )}

      {quiz.questions.map((q, qi) => (
        <div key={q.id} className="p-4 rounded-xl border border-border bg-card" data-testid="quiz-question">
          <p className="font-medium mb-1">{qi + 1}. {q.prompt}</p>
          {q.type === "multi" && <p className="text-[11px] text-muted-foreground mb-2">{t("selectAll")}</p>}
          {q.type !== "text" && (
            <div className="space-y-1.5 mt-2">
              {q.options.map((opt, oi) => {
                const selected = q.type === "single" ? answers[q.id] === oi : (answers[q.id] || []).includes(oi);
                const showResult = graded && graded.breakdown;
                const isCorrectCell = showResult ? graded.breakdown[q.id] : null;
                return (
                  <button key={oi} disabled={!!graded}
                    onClick={() => q.type === "single" ? setSingle(q.id, oi) : toggleMulti(q.id, oi)}
                    data-testid="quiz-option"
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition-colors
                      ${selected ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
                    {q.type === "single"
                      ? (selected ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> : <Circle className="w-4 h-4 text-muted-foreground shrink-0" />)
                      : <div className={`w-4 h-4 rounded border shrink-0 ${selected ? "bg-primary border-primary" : "border-muted-foreground"}`} />}
                    {opt}
                  </button>
                );
              })}
            </div>
          )}
          {q.type === "text" && (
            <Input value={answers[q.id] || ""} disabled={!!graded} onChange={(e) => setText(q.id, e.target.value)}
              placeholder={t("typeAnswer")} className="mt-2" data-testid="quiz-text-answer" />
          )}
          {graded && <p className={`text-xs mt-2 font-medium ${graded.breakdown[q.id] ? "text-emerald-600" : "text-destructive"}`}>{graded.breakdown[q.id] ? "✓ " + t("passed") : "✗ " + t("failed")}</p>}
        </div>
      ))}

      {!graded
        ? <Button onClick={submit} disabled={submitting} data-testid="submit-quiz">{t("submitQuiz")}</Button>
        : <Button variant="outline" onClick={retake} data-testid="retake-quiz"><RotateCcw className="w-4 h-4 mr-1.5" />{t("retakeQuiz")}</Button>}
    </div>
  );
}

export default function CoursePlayer({ courseId, onBack }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null); // {mi, kind, id}

  const load = async () => {
    const { data: d } = await api.get(`/training/courses/${courseId}`);
    setData(d);
    if (!active) {
      const firstMod = d.course.modules[0];
      if (firstMod?.lessons?.length) setActive({ mi: 0, kind: "lesson", id: firstMod.lessons[0].id });
      else if (firstMod?.quiz) setActive({ mi: 0, kind: "quiz", id: firstMod.quiz.id });
    }
    return d;
  };
  useEffect(() => { load(); }, [courseId]); // eslint-disable-line

  if (!data) return <div className="p-8 text-center text-muted-foreground">…</div>;
  const { course, my_progress: mp } = data;
  const completedLessons = new Set(mp.completed_lessons || []);
  const quizResults = mp.quiz_results || {};

  const flatItems = [];
  course.modules.forEach((m, mi) => {
    (m.lessons || []).forEach((l) => flatItems.push({ mi, kind: "lesson", id: l.id }));
    if (m.quiz && m.quiz.questions?.length) flatItems.push({ mi, kind: "quiz", id: m.quiz.id });
  });
  const activeIdx = flatItems.findIndex((it) => it.kind === active?.kind && it.id === active?.id);
  const goNext = () => { if (activeIdx >= 0 && activeIdx < flatItems.length - 1) setActive(flatItems[activeIdx + 1]); };

  const activeModule = active ? course.modules[active.mi] : null;
  const activeLesson = active?.kind === "lesson" ? activeModule?.lessons.find((l) => l.id === active.id) : null;
  const activeQuiz = active?.kind === "quiz" ? activeModule?.quiz : null;

  const markLesson = async () => {
    try {
      await api.post(`/training/courses/${courseId}/lessons/${activeLesson.id}/complete`);
      await load();
      goNext();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadCert = () => {
    if (!mp.certificate_id) return;
    window.open(`${API}/training/certificates/${mp.certificate_id}/pdf?auth=${encodeURIComponent(getAccessToken() || "")}`, "_blank");
  };

  return (
    <div className="h-full flex flex-col">
      <div className="border-b border-border px-4 sm:px-6 py-3 flex items-center gap-3 bg-card">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="player-back"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-lg font-bold truncate">{course.title}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <Progress value={mp.percent} className="h-1.5 w-32" />
            <span className="text-xs text-muted-foreground">{mp.percent}%</span>
          </div>
        </div>
        {mp.completed && mp.certificate_id && (
          <Button onClick={downloadCert} data-testid="download-certificate" className="bg-gradient-to-r from-primary to-purple-500">
            <Award className="w-4 h-4 mr-1.5" />{t("downloadCertificate")}
          </Button>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* curriculum sidebar */}
        <div className="w-72 border-r border-border overflow-y-auto scrollbar-thin bg-card/50 shrink-0 hidden md:block">
          {course.modules.map((m, mi) => (
            <div key={m.id} className="py-1">
              <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{t("module")} {mi + 1}: {m.title}</p>
              {(m.lessons || []).map((l) => {
                const Icon = LTYPE_ICON[l.type] || FileText;
                const done = completedLessons.has(l.id);
                const isActive = active?.kind === "lesson" && active.id === l.id;
                return (
                  <button key={l.id} onClick={() => setActive({ mi, kind: "lesson", id: l.id })} data-testid="curriculum-lesson"
                    className={`w-full text-left px-4 py-2 flex items-center gap-2 text-sm transition-colors ${isActive ? "bg-primary/10 text-primary" : "hover:bg-accent"}`}>
                    {done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Icon className="w-4 h-4 text-muted-foreground shrink-0" />}
                    <span className="truncate">{l.title}</span>
                  </button>
                );
              })}
              {m.quiz && m.quiz.questions?.length > 0 && (() => {
                const r = quizResults[m.quiz.id];
                const isActive = active?.kind === "quiz" && active.id === m.quiz.id;
                return (
                  <button onClick={() => setActive({ mi, kind: "quiz", id: m.quiz.id })} data-testid="curriculum-quiz"
                    className={`w-full text-left px-4 py-2 flex items-center gap-2 text-sm transition-colors ${isActive ? "bg-primary/10 text-primary" : "hover:bg-accent"}`}>
                    {r?.passed ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <ListChecks className="w-4 h-4 text-muted-foreground shrink-0" />}
                    <span className="truncate">{m.quiz.title}</span>
                    {r && <Badge variant="secondary" className="ml-auto text-[9px]">{r.best_score}%</Badge>}
                  </button>
                );
              })()}
            </div>
          ))}
        </div>

        {/* content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4 sm:p-8">
          {activeLesson && (
            <div className="max-w-3xl space-y-4">
              <h2 className="font-display text-2xl font-bold">{activeLesson.title}</h2>
              {activeLesson.type === "video" && (() => {
                const emb = embedUrl(activeLesson.video_url);
                if (activeLesson.video_source === "upload" && activeLesson.file_id)
                  return <video controls className="w-full rounded-xl bg-black" src={fileDownloadUrl(activeLesson.file_id)} data-testid="lesson-video" />;
                if (emb) return <div className="aspect-video rounded-xl overflow-hidden bg-black"><iframe title="video" src={emb} className="w-full h-full" allowFullScreen data-testid="lesson-video" /></div>;
                if (activeLesson.video_url) return <video controls className="w-full rounded-xl bg-black" src={activeLesson.video_url} data-testid="lesson-video" />;
                return <p className="text-muted-foreground text-sm">—</p>;
              })()}
              {activeLesson.type === "pdf" && activeLesson.file_id && (
                <iframe title="pdf" src={fileDownloadUrl(activeLesson.file_id)} className="w-full h-[70vh] rounded-xl border border-border" data-testid="lesson-pdf" />
              )}
              {activeLesson.content && <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap leading-relaxed" data-testid="lesson-text">{activeLesson.content}</div>}
              {(activeLesson.attachments || []).length > 0 && (
                <div className="space-y-1.5 pt-2">
                  {activeLesson.attachments.map((a, i) => (
                    <a key={i} href={fileDownloadUrl(a.file_id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2.5 rounded-lg border border-border hover:bg-accent text-sm" data-testid="lesson-resource">
                      <Download className="w-4 h-4 text-primary" />{a.name}
                    </a>
                  ))}
                </div>
              )}
              <div className="pt-3">
                <Button onClick={markLesson} data-testid="mark-lesson-complete">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />{t("markComplete")}
                </Button>
              </div>
            </div>
          )}
          {activeQuiz && (
            <QuizTaker courseId={courseId} quiz={activeQuiz} result={quizResults[activeQuiz.id]}
              onGraded={() => load()} />
          )}
        </div>
      </div>
    </div>
  );
}
